// The waiver writes a manager makes from a list of claims (WAIVERS.md):
// reorder, withdraw, undo. One copy for the Waivers screen and My Team's
// waivers panel (B-316), so the two say the same thing when the site does
// not answer.
//
// A REAL LEAGUE WRITES THE SERVER (W8), each answered in the server's own
// words and read back when it lands (`data/moves.ts`); the practice league is
// the simulator's.

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import type { ClaimView, TransactionView, WaiversView } from '@fsp/domain';
import { messageOf, type Reply } from '../../data/api';
import { useLeagueSource } from '../../data/leagueSource';
import { reorderClaims, undoMove, withdrawClaim } from '../../data/moves';
import { useSim } from '../../data/SimProvider';
import { tell } from '../../kit/ui';

export interface WaiverWrites {
  /** A write is on its way; the controls that make one wait. */
  busy: boolean;
  /** One claim earlier (-1) or later (1) in my order. */
  moveClaim: (id: string, dir: -1 | 1) => void;
  /** First of my claims, in one move (B-380). */
  claimToTop: (id: string) => void;
  withdraw: (claim: ClaimView) => void;
  undo: (tx: TransactionView) => void;
}

export function useWaiverWrites(view: WaiversView): WaiverWrites {
  const sim = useSim();
  const real = useLeagueSource() === 'api';
  const { leagueId } = useParams();
  // ONE CLAIM WRITE AT A TIME. A reorder is built from the claims on screen,
  // and a second tap before the first answer would build it from a stale list.
  const [busy, setBusy] = useState(false);

  /** A real league's write, one at a time, with the server's sentence when it says no. */
  const write = <T>(
    run: (league: string) => Promise<Reply<T>>,
    done: string,
    unreachable: string,
  ) => {
    if (busy || leagueId === undefined) return;
    setBusy(true);
    void run(leagueId).then((reply) => {
      setBusy(false);
      if (reply.status === 'ok') tell(done);
      else
        tell(
          messageOf(reply, 'That did not go through.', unreachable),
          reply.status === 'refused' ? 'warn' : 'bad',
        );
    });
  };

  const moveClaim = (id: string, dir: -1 | 1) => {
    const ids = view.myClaims.map((c) => c.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    reorder(ids);
  };

  const claimToTop = (id: string) => {
    const ids = view.myClaims.map((c) => c.id);
    if (ids.indexOf(id) <= 0) return;
    reorder([id, ...ids.filter((x) => x !== id)]);
  };

  /** A new order for my claims, saved where the league keeps it. */
  const reorder = (ids: string[]) => {
    if (real) {
      write(
        (league) => reorderClaims(league, { claimIds: ids }),
        'Claim order saved.',
        'Could not reach the site, so we cannot tell whether the new order was saved. Try again: the same order saved twice is the same order.',
      );
      return;
    }
    const err = sim.reorderClaims(ids);
    if (err) tell(err, 'bad');
  };

  const withdraw = (c: ClaimView) => {
    if (real) {
      write(
        (league) => withdrawClaim(league, c.id),
        `Withdrew your claim for ${c.player.name}.`,
        'Could not reach the site, so we cannot tell whether the claim was withdrawn. Try again: if it was, you will be told it is no longer pending.',
      );
      return;
    }
    const err = sim.withdrawClaim(c.id);
    tell(err ?? `Withdrew your claim for ${c.player.name}.`, err ? 'bad' : 'neutral');
  };

  const undo = (tx: TransactionView) => {
    if (real) {
      write(
        (league) => undoMove(league, tx.id),
        'Undone.',
        'Could not reach the site, so we cannot tell whether that was undone. Try again: if it was, you will be told so.',
      );
      return;
    }
    const err = sim.undoTransaction(tx.id);
    tell(err ?? 'Undone.', err ? 'bad' : 'neutral');
  };

  return { busy, moveClaim, claimToTop, withdraw, undo };
}
